<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import UiModal from '@/components/ui/UiModal.vue'
import UiButton from '@/components/ui/UiButton.vue'
import UiField from '@/components/ui/UiField.vue'
import RegardingPicker from '@/components/form/RegardingPicker.vue'
import DateTimeInput from '@/components/form/DateTimeInput.vue'
import NumberInput from '@/components/form/NumberInput.vue'
import { getRecord } from '@/lib/records.js'
import { saveFilling } from '@/lib/saveFilling.js'
import { firstLine } from '@/lib/notes.js'
import { activityTypeIcon, enumLabel, fieldLabel } from '@/lib/labels.js'
import { useMeta } from '@/stores/meta.js'
import { useSave } from '@/composables/useSave.js'

/**
 * A note made an activity (D-347) — a call noted in a hurry as a note: its words copied into the
 * activity, the note kept on it (as a summary after a call is). A call or meeting with a time
 * gone by took place: made closed, with how it ended and how long it took. What the company's
 * rules ask for besides is asked before it is sent (D-343).
 */
const props = defineProps({
  note: { type: Object, required: true },
})
const emit = defineEmits(['close', 'done'])
const { t } = useI18n()
const meta = useMeta()
const { report } = useSave()

const TYPES = ['call', 'meeting', 'task', 'email']
const type = ref('call')
const subject = ref(firstLine(props.note))
const regarding = ref(null)
const reference = ref(null)
const at = ref(props.note.created_at || new Date().toISOString())
const outcome = ref('')
const minutes = ref(null)
const busy = ref(false)

// What the note is on is what the activity is about; a note on an activity: what that one is about.
onMounted(async () => {
  const on = props.note.noteable
  if (!on?.uuid) return
  if (on.entity !== 'activity') {
    regarding.value = { entity: on.entity, uuid: on.uuid }
    reference.value = on
    return
  }
  const activity = (await getRecord('activity', on.uuid).catch(() => null))?.data
  if (activity?.regarding?.uuid) {
    regarding.value = { entity: activity.regarding.entity, uuid: activity.regarding.uuid }
    reference.value = activity.regarding
  }
})

// A call or meeting at a time gone by took place (the user's decision): closed as it is made.
const tookPlace = computed(() => ['call', 'meeting'].includes(type.value) && !!at.value && Date.parse(at.value) <= Date.now())
const outcomes = computed(() => (meta.dictionary('activity_outcome')?.entries || []).filter((e) => !e.meta?.types?.length || e.meta.types.includes(type.value)))
const minutesValid = computed(() => minutes.value == null || (minutes.value >= 0 && minutes.value <= 10080))
const canSave = computed(() => subject.value.trim() && at.value && minutesValid.value && !busy.value)

async function save() {
  if (!canSave.value) return
  busy.value = true
  try {
    const body = {
      type: type.value,
      subject: subject.value.trim(),
      regarding: regarding.value,
      due_at: at.value,
      from_note: props.note.uuid,
      ...(tookPlace.value ? { completed: true, outcome: outcome.value || null, actual_duration_minutes: minutes.value ?? null } : {}),
    }
    const result = await saveFilling('activity', null, body, { label: body.subject })
    if (result.status === 'cancelled') return
    report(result, { success: t(tookPlace.value ? 'notes.convert.doneClosed' : 'notes.convert.done', { type: enumLabel('activity', 'type', type.value) }) })
    if (result.status === 'saved' || result.status === 'queued') emit('done', result)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <UiModal open :title="$t('notes.convert.title')" size="md" @close="emit('close')">
    <div class="space-y-3" data-testid="note-convert">
      <p class="text-sm text-muted">{{ $t('notes.convert.intro') }}</p>
      <div class="grid grid-cols-2 gap-2 sm:grid-cols-4" role="radiogroup" :aria-label="$t('notes.convert.type')">
        <button v-for="k in TYPES" :key="k" type="button" role="radio" :aria-checked="type === k" :data-testid="`note-convert-type-${k}`"
          :class="['flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm', type === k ? 'border-brand-500 bg-brand-50 font-medium text-brand-800 dark:bg-brand-900/30 dark:text-brand-100' : 'border-default text-muted hover:bg-[var(--surface-3)]']"
          @click="type = k; outcome = ''">
          <i :class="['fa-solid', activityTypeIcon(k)]" aria-hidden="true"></i>{{ enumLabel('activity', 'type', k) }}
        </button>
      </div>
      <UiField :label="$t('notes.convert.subject')" required>
        <input v-model="subject" class="input" maxlength="255" data-testid="note-convert-subject" />
      </UiField>
      <UiField :label="fieldLabel('activity', 'regarding')">
        <RegardingPicker v-model="regarding" :reference="reference" />
      </UiField>
      <UiField :label="$t('notes.convert.when')" required>
        <DateTimeInput v-model="at" direction="any" data-testid="note-convert-at" />
      </UiField>
      <!-- gone by: it took place, made closed -->
      <div v-if="tookPlace" class="space-y-3 rounded-lg border border-emerald-300 bg-emerald-50/60 p-3 dark:border-emerald-800 dark:bg-emerald-900/20" data-testid="note-convert-took-place">
        <p class="text-sm"><i class="fa-solid fa-check mr-1 text-emerald-600" aria-hidden="true"></i>{{ $t('notes.convert.tookPlace') }}</p>
        <UiField :label="$t('activity.outcome')">
          <select v-model="outcome" class="input" data-testid="note-convert-outcome">
            <option value="">{{ $t('common.select') }}</option>
            <option v-for="e in outcomes" :key="e.key" :value="e.key">{{ meta.localized(e.label) }}</option>
          </select>
        </UiField>
        <UiField :label="$t('activity.actualDuration')" :error="minutesValid ? '' : $t('activity.durationInvalid')">
          <div class="flex items-center gap-2">
            <NumberInput v-model="minutes" integer :min="0" :max="10080" class="w-28" data-testid="note-convert-minutes" />
            <span class="text-sm text-muted">{{ $t('activity.minutes') }}</span>
          </div>
        </UiField>
      </div>
      <p class="text-xs text-muted">{{ $t('notes.convert.kept') }}</p>
    </div>
    <template #footer>
      <UiButton @click="emit('close')">{{ $t('common.cancel') }}</UiButton>
      <UiButton variant="primary" icon="fa-arrow-right-arrow-left" :loading="busy" :disabled="!canSave" data-testid="note-convert-confirm" @click="save">{{ $t('notes.convert.confirm') }}</UiButton>
    </template>
  </UiModal>
</template>
